import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Anchor, Fish, Waves, Droplet, Compass, Ship, LifeBuoy, Map } from 'lucide-react';

const ScubaDiving: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Deep Blue Dive Center";
    
    return (
        <div className="min-h-screen bg-gradient-to-b from-[#00b4d8] to-[#03045e] font-sans text-white selection:bg-white selection:text-[#0077b6] relative overflow-hidden">
            
            {/* Bubble Particles (CSS Animation simulation) */}
            <div className="fixed inset-0 pointer-events-none z-0">
                 {[...Array(20)].map((_, i) => (
                    <div 
                        key={i}
                        className="absolute bg-white/10 rounded-full border border-white/20 animate-pulse"
                        style={{
                            bottom: '-50px',
                            left: `${Math.random() * 100}%`,
                            width: `${Math.random() * 40 + 10}px`,
                            height: `${Math.random() * 40 + 10}px`,
                            animationDuration: `${Math.random() * 10 + 5}s`,
                            animationDelay: `${Math.random() * 5}s`,
                            transform: `translateY(-${Math.random() * 100 + 100}vh)` // Simplified upwards drift
                        }}
                    />
                 ))}
            </div>

            {/* Navbar */}
            <nav className="fixed w-full z-50 px-6 py-6 border-b border-white/10 bg-white/10 backdrop-blur-md">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="bg-white text-[#0077b6] p-2 rounded-full">
                            <Anchor size={24} />
                        </div>
                        <span className="font-bold text-xl tracking-tight uppercase shadow-black drop-shadow-md">{schoolName}</span>
                    </div>
                    
                    <div className="hidden md:flex gap-8 font-bold text-sm tracking-wider uppercase text-white/90">
                        <a href="#" className="hover:text-white hover:drop-shadow-[0_0_5px_rgba(255,255,255,0.8)] transition-all">Courses</a>
                        <a href="#" className="hover:text-white hover:drop-shadow-[0_0_5px_rgba(255,255,255,0.8)] transition-all">Dive Sites</a>
                        <a href="#" className="hover:text-white hover:drop-shadow-[0_0_5px_rgba(255,255,255,0.8)] transition-all">Gear Shop</a>
                    </div>
                    
                    <Button className="bg-[#caf0f8] text-[#03045e] hover:bg-white hover:text-[#0077b6] font-bold rounded-full px-6 shadow-lg shadow-blue-900/40">
                        Book Dive
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 pt-48 pb-32 px-6 flex flex-col items-center text-center">
                <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-blue-500/20 rounded-full border border-blue-400/30 backdrop-blur-sm text-xs font-bold uppercase tracking-widest text-[#caf0f8]">
                    <Waves size={14} /> PADI 5-Star Instructor Center
                </div>
                
                <h1 className="text-6xl md:text-9xl font-black mb-8 leading-none tracking-tighter drop-shadow-2xl">
                    DIVING <br/> 
                    <span className="text-transparent bg-clip-text bg-gradient-to-t from-[#90e0ef] to-white">DEEPER</span>
                </h1>
                
                <p className="text-xl md:text-2xl text-[#caf0f8] max-w-2xl mx-auto mb-12 drop-shadow-md font-medium">
                    Explore the silent world beneath the waves. From open water certification to technical wrecks, your adventure starts here.
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 w-full max-w-4xl">
                     {[
                         { icon: LifeBuoy, label: "Beginner" },
                         { icon: Compass, label: "Advanced" },
                         { icon: Ship, label: "Wrecks" },
                         { icon: Fish, label: "Marine Bio" }
                     ].map((item, i) => (
                         <div key={i} className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl flex flex-col items-center gap-3 hover:bg-white/20 transition-colors cursor-pointer group">
                             <item.icon size={32} className="text-[#caf0f8] group-hover:scale-110 transition-transform" />
                             <span className="font-bold text-sm uppercase tracking-wide">{item.label}</span>
                         </div>
                     ))}
                </div>
            </header>

            {/* Content / Info Wave */}
            <div className="relative z-10 w-full bg-white text-[#03045e] py-20 px-6 rounded-t-[3rem] mt-20 shadow-[0_-20px_60px_rgba(0,0,0,0.3)]">
                 <div className="max-w-6xl mx-auto">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                         <div>
                             <h2 className="text-4xl font-bold mb-6">Discover a New World</h2>
                             <p className="text-lg text-slate-600 leading-relaxed mb-8">
                                 Breathing underwater is a superpower. Our experienced instructors ensure safety while guiding you through vibrant coral reefs and mysterious underwater landscapes.
                             </p>
                             <ul className="space-y-4 mb-8">
                                 <li className="flex items-center gap-3 font-bold text-[#0077b6]">
                                    <div className="w-6 h-6 rounded-full bg-[#caf0f8] flex items-center justify-center"><span className="text-xs">✓</span></div>
                                    Small Group Sizes
                                 </li>
                                 <li className="flex items-center gap-3 font-bold text-[#0077b6]">
                                    <div className="w-6 h-6 rounded-full bg-[#caf0f8] flex items-center justify-center"><span className="text-xs">✓</span></div>
                                    Top-tier Scubapro Gear
                                 </li>
                                 <li className="flex items-center gap-3 font-bold text-[#0077b6]">
                                    <div className="w-6 h-6 rounded-full bg-[#caf0f8] flex items-center justify-center"><span className="text-xs">✓</span></div>
                                    Eco-conscious Standards
                                 </li>
                             </ul>
                             <Button size="lg" className="rounded-full bg-[#0077b6] hover:bg-[#023e8a] text-white px-8">
                                 View Schedule
                             </Button>
                         </div>
                         <div className="relative h-96 rounded-3xl overflow-hidden shadow-2xl skew-y-3 hover:skew-y-0 transition-transform duration-700">
                             <img 
                                src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=2070&auto=format&fit=crop" 
                                alt="Diver" 
                                className="w-full h-full object-cover"
                             />
                             <div className="absolute inset-0 bg-blue-500/20 mix-blend-overlay"></div>
                         </div>
                     </div>
                 </div>
            </div>

             {/* Footer */}
            <footer className="bg-[#03045e] text-white py-20 px-6">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
                     <div className="flex items-center gap-4">
                         <Anchor className="text-[#48cae4]" />
                         <span className="font-bold text-xl">{schoolName}</span>
                     </div>
                     <div className="flex gap-8 text-sm font-bold text-[#caf0f8]">
                         <a href="#" className="hover:text-white">Locations</a>
                         <a href="#" className="hover:text-white">Medical Form</a>
                         <a href="#" className="hover:text-white">Insurance</a>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default ScubaDiving;
